export interface StripeCharge {
  id: string;
  amount: number;
  currency: string;
  customer: string | null;
  created: number;
  status: string;
  receipt_email: string | null;
  description: string | null;
  card_brand: string | null;
  card_funding: string | null;
}

export interface StripeCustomerData {
  id: string;
  email: string | null;
  name: string | null;
  phone: string | null;
}

export interface StripeAccountData {
  id: string;
  business_profile: {
    name: string | null;
    support_email: string | null;
    url: string | null;
  };
  settings: {
    branding: {
      icon: string | null;
      logo: string | null;
      primary_color: string | null;
    };
  };
  email: string | null;
}

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || 'https://ufdfviawquynyheupoxe.supabase.co';
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InVmZGZ2aWF3cXV5bnloZXVwb3hlIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzE4NjcxNjcsImV4cCI6MjA4NzQ0MzE2N30.5nJmzIzClkhynkZcD2-Jgbv-mqVl5qswFm9u2KxDF-w';

export async function fetchStripeCharges(
  stripeSecretKey: string
): Promise<StripeCharge[]> {
  const apiUrl = `${SUPABASE_URL}/functions/v1/stripe-sync`;

  const response = await fetch(apiUrl, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      stripeSecretKey,
      action: 'fetchCharges',
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch Stripe charges');
  }

  return await response.json();
}

export async function fetchStripeCustomer(
  stripeSecretKey: string,
  customerId: string
): Promise<StripeCustomerData | null> {
  const apiUrl = `${SUPABASE_URL}/functions/v1/stripe-sync`;

  const response = await fetch(apiUrl, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      stripeSecretKey,
      action: 'fetchCustomer',
      customerId,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch Stripe customer');
  }

  return await response.json();
}

export async function fetchStripeNetRevenue(
  stripeSecretKey: string,
  startTimestamp: number,
  endTimestamp: number
): Promise<{ net_revenue: number; transaction_count: number }> {
  const apiUrl = `${SUPABASE_URL}/functions/v1/stripe-sync`;

  const response = await fetch(apiUrl, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      stripeSecretKey,
      action: 'fetchNetRevenue',
      startTimestamp,
      endTimestamp,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch Stripe net revenue');
  }

  return await response.json();
}

export interface StripeTransaction {
  id: string;
  amount: number;
  fee: number;
  net: number;
  currency: string;
  type: string;
  status: string;
  description: string;
  created: number;
  source_id: string | null;
  customer_email: string | null;
  customer_name: string | null;
  reporting_category: string | null;
}

export async function fetchStripeTransactions(
  stripeSecretKey: string,
  startTimestamp: number,
  endTimestamp: number
): Promise<StripeTransaction[]> {
  const apiUrl = `${SUPABASE_URL}/functions/v1/stripe-sync`;

  const response = await fetch(apiUrl, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      stripeSecretKey,
      action: 'fetchTransactions',
      startTimestamp,
      endTimestamp,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch Stripe transactions');
  }

  return await response.json();
}

export interface StripeDailyBalance {
  date: string;
  gross: number;
  fees: number;
  net: number;
  count: number;
  refunds: number;
}

export interface StripePayout {
  id: string;
  amount: number;
  currency: string;
  status: string;
  arrival_date: number;
  created: number;
  method: string;
  type: string;
}

export interface StripeRefund {
  id: string;
  amount: number;
  currency: string;
  status: string;
  reason: string | null;
  created: number;
}

export async function fetchStripeDailyBalances(
  stripeSecretKey: string,
  startTimestamp: number,
  endTimestamp: number
): Promise<StripeDailyBalance[]> {
  const apiUrl = `${SUPABASE_URL}/functions/v1/stripe-sync`;

  const response = await fetch(apiUrl, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      stripeSecretKey,
      action: 'fetchDailyBalances',
      startTimestamp,
      endTimestamp,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch daily balances');
  }

  return await response.json();
}

export async function fetchStripePayouts(
  stripeSecretKey: string,
  startTimestamp: number,
  endTimestamp: number
): Promise<StripePayout[]> {
  const apiUrl = `${SUPABASE_URL}/functions/v1/stripe-sync`;

  const response = await fetch(apiUrl, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      stripeSecretKey,
      action: 'fetchPayouts',
      startTimestamp,
      endTimestamp,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch payouts');
  }

  return await response.json();
}

export async function fetchStripeRefunds(
  stripeSecretKey: string,
  startTimestamp: number,
  endTimestamp: number
): Promise<{ refunds: StripeRefund[] }> {
  const apiUrl = `${SUPABASE_URL}/functions/v1/stripe-sync`;

  const response = await fetch(apiUrl, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      stripeSecretKey,
      action: 'fetchDisputesAndRefunds',
      startTimestamp,
      endTimestamp,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch refunds');
  }

  return await response.json();
}

export interface StripeBalanceAmount {
  amount: number;
  currency: string;
}

export interface StripeUpcomingPayout {
  id: string;
  amount: number;
  currency: string;
  status: string;
  arrival_date: number;
  created: number;
  estimated?: boolean;
}

export interface StripeBalanceData {
  available: StripeBalanceAmount[];
  pending: StripeBalanceAmount[];
  upcoming_payouts: StripeUpcomingPayout[];
}

export async function fetchStripeBalance(
  stripeSecretKey: string
): Promise<StripeBalanceData> {
  const apiUrl = `${SUPABASE_URL}/functions/v1/stripe-sync`;

  const response = await fetch(apiUrl, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      stripeSecretKey,
      action: 'fetchBalance',
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch Stripe balance');
  }

  return await response.json();
}

export interface StripeDashboardMetrics {
  gross_volume: number;
  total_fees: number;
  total_refunds: number;
  net_revenue: number;
  charge_count: number;
  refund_count: number;
  debit_gross: number;
  debit_count: number;
  credit_gross: number;
  credit_count: number;
}

export async function fetchStripeDashboardMetrics(
  stripeSecretKey: string,
  startTimestamp: number,
  endTimestamp: number
): Promise<StripeDashboardMetrics> {
  const apiUrl = `${SUPABASE_URL}/functions/v1/stripe-sync`;

  const response = await fetch(apiUrl, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      stripeSecretKey,
      action: 'fetchDashboardMetrics',
      startTimestamp,
      endTimestamp,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch dashboard metrics');
  }

  return await response.json();
}

export async function fetchStripeAccount(
  stripeSecretKey: string
): Promise<StripeAccountData> {
  const apiUrl = `${SUPABASE_URL}/functions/v1/stripe-sync`;

  const response = await fetch(apiUrl, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      stripeSecretKey,
      action: 'fetchAccount',
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch Stripe account');
  }

  return await response.json();
}
